import React, { useEffect, useRef, useState } from 'react';
import PhoneMockup from './PhoneMockup';
import WheelGame from './WheelGame';
import './GameCreator.css';

// ─── Endpoints du vrai dashboard commerçant (même logique que /singup) ────
const DASHBOARD_BASE_URL = 'https://commercant.heypulse.fr';
const DASHBOARD_SIGNUP_URL = `${DASHBOARD_BASE_URL}/backend/auth/signup_prospect.php`;
const DASHBOARD_UPLOAD_URL = `${DASHBOARD_BASE_URL}/backend/companie/upload.php`;
const DASHBOARD_LOGIN_URL = `${DASHBOARD_BASE_URL}/login`;
// URL publique du jeu (embarquée dans l'iframe après création)
// Format réel : https://play.heypulse.fr/index.php?id={slug}
const PUBLIC_GAME_BASE_URL = 'https://play.heypulse.fr/index.php';
// URL "lead" historique (garde le CRM / Brevo / mail interne — sans email prospect)
const LEAD_URL = 'https://outils.heypulse.fr/envoi_formulaire.php';

const initialData = {
  prenom: '',
  email: '',
  telephone: '',
  nom_restaurant: '',
  adresse_restaurant: '',
  google_place_id: '',
  parties: 100,
  gain: 30,
  lots: ['Dessert offert', '', ''],
  logo: null,
};

const TOTAL_STEPS = 3;

// Génère un slug identique à celui produit par le backend signup.php
function generateSlug(name) {
  if (!name) return '';
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // retire les accents
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

// Génère un mot de passe fort aléatoire (le user le changera via l'email)
function generatePassword(len = 14) {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
  const arr = new Uint32Array(len);
  window.crypto.getRandomValues(arr);
  let out = '';
  for (let i = 0; i < len; i++) out += chars[arr[i] % chars.length];
  return out;
}

const GameCreator = ({ onCreated }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(initialData);
  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [createdSlug, setCreatedSlug] = useState(null);
  const [emailSent, setEmailSent] = useState(false);

  const autocompleteRef = useRef(null);
  const rootRef = useRef(null);

  // ─── Google Places autocomplete (étape 1) ───────────────────────────────
  useEffect(() => {
    if (step !== 1) return;
    if (!autocompleteRef.current) return;

    let ac = null;
    let listener = null;
    let cancelled = false;

    const attach = () => {
      if (cancelled) return false;
      if (!window.google || !window.google.maps || !window.google.maps.places || !autocompleteRef.current) {
        return false;
      }

      ac = new window.google.maps.places.Autocomplete(autocompleteRef.current, {
        types: ['establishment'],
        componentRestrictions: { country: ['fr', 'be', 'ch', 'ma', 'lu'] },
        fields: ['name', 'formatted_address', 'place_id'],
      });

      listener = ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (place && place.name) {
          setFormData((prev) => ({
            ...prev,
            nom_restaurant: place.name,
            adresse_restaurant: place.formatted_address || prev.adresse_restaurant,
            google_place_id: place.place_id || prev.google_place_id,
          }));
          setErrors((prev) => ({ ...prev, nom_restaurant: null }));
        }
      });

      autocompleteRef.current.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') e.preventDefault();
      });
      return true;
    };

    if (!attach()) {
      const interval = setInterval(() => {
        if (attach()) clearInterval(interval);
      }, 200);
      const timeout = setTimeout(() => clearInterval(interval), 10000);
      return () => {
        cancelled = true;
        clearInterval(interval);
        clearTimeout(timeout);
        if (window.google && window.google.maps && listener) {
          window.google.maps.event.removeListener(listener);
        }
      };
    }

    return () => {
      cancelled = true;
      if (window.google && window.google.maps && listener) {
        window.google.maps.event.removeListener(listener);
      }
    };
  }, [step]);

  // Prévisualisation du logo
  useEffect(() => {
    if (!formData.logo) {
      setLogoPreview(null);
      return;
    }
    const url = URL.createObjectURL(formData.logo);
    setLogoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [formData.logo]);

  const update = (field) => (e) => {
    const value = e && e.target ? e.target.value : e;
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const updateLot = (i, value) => {
    setFormData((prev) => {
      const next = [...prev.lots];
      next[i] = value;
      return { ...prev, lots: next };
    });
  };

  const addLot = () => setFormData((prev) => ({ ...prev, lots: [...prev.lots, ''] }));

  const removeLot = (i) => {
    setFormData((prev) => {
      if (prev.lots.length <= 1) return prev;
      return { ...prev, lots: prev.lots.filter((_, idx) => idx !== i) };
    });
  };

  const handleLogo = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) setFormData((prev) => ({ ...prev, logo: file }));
  };

  const validateStep = () => {
    const e = {};
    if (step === 1) {
      if (!formData.nom_restaurant.trim()) e.nom_restaurant = 'Cherchez et sélectionnez votre commerce.';
      else if (!formData.google_place_id) {
        e.nom_restaurant = 'Sélectionnez votre commerce dans la liste Google (ne tapez pas seulement le nom).';
      }
    }
    if (step === 2) {
      if (!formData.prenom.trim()) e.prenom = 'Votre prénom est requis.';
      if (!formData.email.trim() || !/^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(formData.email))
        e.email = 'Email invalide.';
      if (!formData.telephone.trim() || !/^\+?[\d\s]{7,20}$/.test(formData.telephone))
        e.telephone = 'Numéro invalide.';
    }
    if (step === 3) {
      const validLots = formData.lots.filter((l) => l.trim());
      if (validLots.length === 0) e.lots = 'Ajoutez au moins un cadeau.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validateStep()) return;
    if (step < TOTAL_STEPS) setStep(step + 1);
    else submit();
  };

  const back = () => setStep((s) => Math.max(1, s - 1));

  // ─── Envoi vers le CRM interne (Brevo + email interne) ──────────────────
  const sendLeadToCRM = async (password) => {
    try {
      const payload = new FormData();
      payload.append('add', '1');
      payload.append('send_confirmation_email', '0');
      payload.append('prenom', formData.prenom);
      payload.append('email', formData.email);
      payload.append('telephone', formData.telephone);
      payload.append('nom_restaurant', formData.nom_restaurant);
      payload.append('adresse_restaurant', formData.adresse_restaurant);
      payload.append('parties', String(formData.parties || 100));
      payload.append('gain', String(formData.gain || 30));
      formData.lots.filter((l) => l.trim()).forEach((lot) => payload.append('lots[]', lot));
      if (formData.logo) payload.append('logo', formData.logo);

      await fetch(LEAD_URL, { method: 'POST', body: payload });
    } catch (err) {
      console.warn('CRM lead sync failed (non bloquant):', err);
    }
  };

  // ─── Upload du logo (même endpoint que /singup) ─────────────────────────
  const uploadLogo = async () => {
    if (!formData.logo) return '';
    try {
      const fd = new FormData();
      fd.append('image', formData.logo);
      const tempId = Date.now();
      const response = await fetch(`${DASHBOARD_UPLOAD_URL}?id=${tempId}`, {
        method: 'POST',
        body: fd,
      });
      const data = await response.json();
      if (data && data.success && data.imageUrl) {
        return `${DASHBOARD_BASE_URL}/backend/companie/${data.imageUrl}`;
      }
      console.warn('Upload logo échoué:', data);
      return '';
    } catch (err) {
      console.warn('Upload logo erreur (non bloquant):', err);
      return '';
    }
  };

  // ─── Création réelle du compte via signup_prospect.php (endpoint dédié lead)
  const createMerchantAccount = async (password) => {
    // 1. Upload logo d'abord (comme Singup.vue)
    const logoUrl = await uploadLogo();

    const lots = formData.lots.map((l) => l.trim()).filter(Boolean);
    const parties = Math.max(1, parseInt(formData.parties, 10) || 100);
    const gain = Math.max(0, Math.min(parties, parseInt(formData.gain, 10) || 0));

    const payload = {
      email: formData.email.trim(),
      password,
      prenom: formData.prenom.trim(),
      telephone: formData.telephone.trim(),
      // Lots personnalisés → injectés dans les slices de la roue
      lots,
      // Probabilité de gain (même logique que le dashboard /jeu)
      parties,
      gain,
      establishment: {
        nom: formData.nom_restaurant.trim(),
        adresse: formData.adresse_restaurant.trim() || formData.nom_restaurant.trim(),
        telephone: formData.telephone.trim(),
        site: '',
        logo: logoUrl,
        google_place_id: formData.google_place_id || '',
        google_review_url: formData.google_place_id
          ? `https://search.google.com/local/writereview?placeid=${formData.google_place_id}`
          : '',
      },
    };

    const response = await fetch(DASHBOARD_SIGNUP_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
    });

    const raw = await response.text();
    let json = null;
    try {
      json = JSON.parse(raw);
    } catch (_) {
      throw new Error(`Réponse inattendue du serveur : ${raw.slice(0, 120)}`);
    }
    if (!response.ok || json.status !== 'success') {
      throw new Error(json.message || 'Erreur inconnue lors de la création du compte.');
    }
    return json;
  };

  const submit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setErrors({});

    const password = generatePassword();
    const fallbackSlug = generateSlug(formData.nom_restaurant);

    try {
      // Crée le compte + envoie UN seul email HeyPulse (confirmation, dashboard, mdp, jeu)
      const result = await createMerchantAccount(password);
      const slug = result.slug || fallbackSlug;

      // CRM interne (Brevo + email équipe) — PAS d'email au prospect
      sendLeadToCRM(password);

      setCreatedSlug(slug);
      setEmailSent(result.email_sent !== false);
      setSubmitted(true);

      if (typeof window.fbq === 'function') window.fbq('track', 'Lead');
      if (onCreated) onCreated({ ...formData, slug, password, etablissement_id: result.etablissement_id });

      setTimeout(() => {
        if (rootRef.current) rootRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    } catch (err) {
      console.error('Erreur création du jeu :', err);
      const msg = err && err.message ? err.message : "Une erreur s'est produite. Réessayez.";
      setErrors({ submit: msg });
      alert(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="create-game" className="gc-section" ref={rootRef}>
      <div className="gc-header">
        <div className="gc-tag">Essai commerçant</div>
        <h2 className="gc-title">
          {submitted ? (
            <>Votre jeu HeyPulse est <span>actif</span></>
          ) : (
            <>Configurez votre jeu <span>maintenant</span></>
          )}
        </h2>
        <p className="gc-subtitle">
          {submitted ? (
            <>L&apos;aperçu ci-contre montre ce que vos clients voient sur téléphone.</>
          ) : (
            <>
              Indiquez votre commerce, vos lots et votre logo — on génère la page de jeu.
              <br />
              <strong>15 jours offerts · Sans carte bancaire</strong>
            </>
          )}
        </p>
      </div>

      <div className={`gc-grid ${submitted ? 'gc-grid-with-preview' : 'gc-grid-form-only'}`}>
        {/* ─── Colonne gauche : formulaire OU récap post-inscription ───── */}
        <div className="gc-form-col">
          {!submitted ? (
            <div className="gc-form-card">
              <div className="gc-progress-wrap">
                <div className="gc-progress-track">
                  <div
                    className="gc-progress-bar"
                    style={{ width: `${((step - 1) / TOTAL_STEPS) * 100 + 20}%` }}
                  />
                </div>
                <div className="gc-progress-meta">
                  <span>Étape {step} / {TOTAL_STEPS}</span>
                  <span className="gc-progress-timer">Quelques secondes</span>
                </div>
              </div>

              {step === 1 && (
                <div className="gc-step">
                  <div className="gc-step-header">
                    <span className="gc-step-num">1</span>
                    <h3>Votre commerce</h3>
                  </div>
                  <p className="gc-help">
                    Cherchez le nom de votre commerce dans la liste Google et
                    <strong> sélectionnez-le</strong> — c'est obligatoire pour relier votre fiche Google avis.
                  </p>

                  <div className="gc-field">
                    <label>Nom du commerce</label>
                    <input
                      ref={autocompleteRef}
                      type="text"
                      placeholder="Ex: Bunch Nantes"
                      defaultValue={formData.nom_restaurant}
                    />
                    {errors.nom_restaurant && <div className="gc-error">{errors.nom_restaurant}</div>}
                  </div>

                  {formData.nom_restaurant && (
                    <div className="gc-detected">
                      <div className="gc-detected-row">
                        <span className="gc-detected-label">Commerce</span>
                        <input
                          type="text"
                          value={formData.nom_restaurant}
                          onChange={update('nom_restaurant')}
                        />
                      </div>
                      <div className="gc-detected-row">
                        <span className="gc-detected-label">Adresse</span>
                        <input
                          type="text"
                          value={formData.adresse_restaurant}
                          onChange={update('adresse_restaurant')}
                          placeholder="Adresse du commerce"
                        />
                      </div>
                      {formData.google_place_id && (
                        <div className="gc-detected-check">
                          ✓ Fiche Google détectée — les avis seront envoyés directement dessus
                        </div>
                      )}
                    </div>
                  )}

                  <div className="gc-actions">
                    <button type="button" className="gc-btn gc-btn-primary" onClick={next}>
                      Continuer
                    </button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="gc-step">
                  <div className="gc-step-header">
                    <span className="gc-step-num">2</span>
                    <h3>Vos coordonnées</h3>
                  </div>
                  <p className="gc-help">
                    On crée immédiatement votre compte commerçant. Vous recevrez votre lien de connexion par email.
                  </p>

                  <div className="gc-field">
                    <label>Prénom</label>
                    <input
                      type="text"
                      placeholder="Ex: Julien"
                      value={formData.prenom}
                      onChange={update('prenom')}
                    />
                    {errors.prenom && <div className="gc-error">{errors.prenom}</div>}
                  </div>

                  <div className="gc-field">
                    <label>Email — c'est votre identifiant</label>
                    <input
                      type="email"
                      placeholder="vous@commerce.fr"
                      value={formData.email}
                      onChange={update('email')}
                    />
                    {errors.email && <div className="gc-error">{errors.email}</div>}
                  </div>

                  <div className="gc-field">
                    <label>Téléphone</label>
                    <input
                      type="tel"
                      placeholder="06 12 34 56 78"
                      value={formData.telephone}
                      onChange={update('telephone')}
                    />
                    {errors.telephone && <div className="gc-error">{errors.telephone}</div>}
                  </div>

                  <div className="gc-actions">
                    <button type="button" className="gc-btn gc-btn-ghost" onClick={back}>Retour</button>
                    <button type="button" className="gc-btn gc-btn-primary" onClick={next}>
                      Continuer
                    </button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="gc-step">
                  <div className="gc-step-header">
                    <span className="gc-step-num">3</span>
                    <h3>Vos cadeaux &amp; votre logo</h3>
                  </div>
                  <p className="gc-help">
                    Ajoutez vos cadeaux et réglez la fréquence des gains — comme sur le dashboard.
                  </p>

                  <div className="gc-field">
                    <label>Cadeaux à faire gagner</label>
                    <div className="gc-lots">
                      {formData.lots.map((lot, i) => (
                        <div key={i} className="gc-lot-row">
                          <span className="gc-lot-index">🎁 {i + 1}</span>
                          <input
                            type="text"
                            placeholder={
                              i === 0 ? 'Ex: Dessert offert'
                                : i === 1 ? 'Ex: Café gratuit'
                                : 'Ex: -10% sur l\'addition'
                            }
                            value={lot}
                            onChange={(e) => updateLot(i, e.target.value)}
                          />
                          {formData.lots.length > 1 && (
                            <button
                              type="button"
                              className="gc-lot-remove"
                              onClick={() => removeLot(i)}
                              aria-label="Supprimer ce lot"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <button type="button" className="gc-lot-add" onClick={addLot}>
                      + Ajouter un cadeau
                    </button>
                    {errors.lots && <div className="gc-error">{errors.lots}</div>}
                  </div>

                  <div className="gc-field">
                    <label>Logo de votre commerce (optionnel)</label>
                    <label className="gc-logo-drop">
                      <input type="file" accept="image/*" onChange={handleLogo} />
                      {logoPreview ? (
                        <img src={logoPreview} alt="Aperçu du logo" />
                      ) : (
                        <div className="gc-logo-drop-empty">
                          <div className="gc-logo-drop-icon">📤</div>
                          <div className="gc-logo-drop-text">Cliquez pour ajouter votre logo</div>
                          <div className="gc-logo-drop-hint">PNG, JPG · Vous pourrez le changer dans votre dashboard</div>
                        </div>
                      )}
                    </label>
                  </div>

                  <div className="gc-field">
                    <label>Probabilité de gain</label>
                    <div className="gc-prob-info">
                      <span className="gc-prob-info-icon" aria-hidden="true">i</span>
                      <p>
                        Sur <strong>{Math.max(1, parseInt(formData.parties, 10) || 0)}</strong> parties jouées,
                        environ <strong>{Math.max(0, parseInt(formData.gain, 10) || 0)}</strong> gains sont distribués,
                        soit une probabilité de{' '}
                        <strong>
                          {(() => {
                            const p = Math.max(1, parseInt(formData.parties, 10) || 1);
                            const g = Math.max(0, Math.min(p, parseInt(formData.gain, 10) || 0));
                            return ((g / p) * 100).toFixed(2);
                          })()}
                          %
                        </strong>
                        .
                      </p>
                    </div>
                    <div className="gc-optional-row">
                      <div className="gc-field gc-field-half" style={{ marginBottom: 0 }}>
                        <label>Nombre de parties (base)</label>
                        <input
                          type="number"
                          min="1"
                          max="10000"
                          value={formData.parties}
                          onChange={(e) => {
                            const parties = Math.max(1, parseInt(e.target.value, 10) || 1);
                            setFormData((prev) => ({
                              ...prev,
                              parties,
                              gain: Math.min(prev.gain, parties),
                            }));
                          }}
                        />
                      </div>
                      <div className="gc-field gc-field-half" style={{ marginBottom: 0 }}>
                        <label>Nombre de gains moyens</label>
                        <input
                          type="number"
                          min="0"
                          max={formData.parties || 10000}
                          value={formData.gain}
                          onChange={(e) => {
                            const parties = Math.max(1, parseInt(formData.parties, 10) || 1);
                            const gain = Math.max(0, Math.min(parties, parseInt(e.target.value, 10) || 0));
                            setFormData((prev) => ({ ...prev, gain }));
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {errors.submit && <div className="gc-error gc-error-block">{errors.submit}</div>}

                  <div className="gc-actions">
                    <button type="button" className="gc-btn gc-btn-ghost" onClick={back} disabled={isSubmitting}>
                      Retour
                    </button>
                    <button
                      type="button"
                      className="gc-btn gc-btn-primary"
                      onClick={next}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Création en cours...' : 'Créer mon jeu gratuitement'}
                    </button>
                  </div>

                  <div className="gc-reassure">
                    ✓ 15 jours gratuits · ✓ Sans CB · ✓ Résiliation en 1 clic
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="gc-success-card">
              <div className="gc-success-check">✓</div>
              <h3>Votre compte est prêt, {formData.prenom || ''} !</h3>
              <p>
                On vient de créer le compte commerçant pour <strong>{formData.nom_restaurant}</strong>{' '}
                (identifiant : <strong>{formData.email}</strong>).
              </p>

              <div className="gc-inbox-note">
                <div className="gc-inbox-icon">📧</div>
                <div>
                  <strong>
                    {emailSent
                      ? 'Un email HeyPulse vient de partir dans votre boîte mail.'
                      : 'Vérifiez votre boîte mail (et vos spams).'}
                  </strong>
                  <p style={{ margin: '8px 0 0', fontSize: '0.9rem', lineHeight: 1.55, color: '#333' }}>
                    Il contient tout ce dont vous avez besoin :
                  </p>
                  <ol>
                    <li>Confirmation d'inscription</li>
                    <li>Lien pour <strong>créer votre mot de passe</strong></li>
                    <li>Lien vers votre <strong>dashboard commerçant</strong></li>
                    <li>Lien pour <strong>tester votre jeu</strong></li>
                  </ol>
                </div>
              </div>

              <ul className="gc-success-list">
                <li>✅ Compte commerçant créé — 15 jours offerts</li>
                <li>✅ Fiche établissement configurée{formData.google_place_id ? ' + fiche Google reliée' : ''}</li>
                <li>✅ {formData.lots.filter((l) => l.trim()).length} cadeaux dans votre roue</li>
              </ul>

              <a
                className="gc-btn gc-btn-primary gc-btn-block"
                href={buildGameUrl(createdSlug)}
                target="_blank"
                rel="noreferrer"
              >
                Voir mon jeu en plein écran ↗
              </a>
              <a
                className="gc-btn gc-btn-ghost gc-btn-block"
                href={DASHBOARD_LOGIN_URL}
                target="_blank"
                rel="noreferrer"
              >
                Accéder à mon dashboard commerçant
              </a>

              <div className="gc-reassure">
                Un membre de l'équipe vous contactera sous 48h pour commander vos flyers personnalisés.
              </div>
            </div>
          )}
        </div>

        {/* ─── Aperçu du jeu (comme le dashboard /jeu) : uniquement après création ─ */}
        {submitted && (
          <div className="gc-phone-col">
            <div className="gc-phone-sticky">
              <PhoneMockup statusLabel="✓ Aperçu en direct">
                <WheelGame
                  prizes={formData.lots}
                  logo={logoPreview}
                  centerSrc="center.png"
                  background="#ffffff"
                  businessName={formData.nom_restaurant}
                  message="PARTICIPEZ À NOTRE JEU ET TENTEZ DE GAGNER UN CADEAU !"
                />
              </PhoneMockup>
              <div className="gc-phone-caption gc-phone-caption-live">
                Aperçu de votre jeu —{' '}
                <a href={buildGameUrl(createdSlug)} target="_blank" rel="noreferrer">
                  l&apos;ouvrir sur mobile ↗
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

function buildGameUrl(slug) {
  if (!slug) return 'https://play.heypulse.fr';
  return `${PUBLIC_GAME_BASE_URL}?id=${encodeURIComponent(slug)}`;
}

export default GameCreator;
